/* eslint-disable consistent-return */

import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import faker from 'faker';
import { isNil, get, map } from 'lodash';

import { ATLAS_TEMPLATES, ATLAS_ELEMENTS } from '../../ui/atoms/AtlasAtoms/atlas-elements';

/**
 * An atlas object for ipsilum maps.
 *
 * @typedef {object} Atlas
 * @property {string} _id - The atlas id.
 * @property {atlasElementAndProps[]} atlasStructure - The selected structure for the atlas type.
 * @property {string} atlasType - The atlas type. One of [templateScorecard].
 * @property {string} createdAt - The date when it was created.
 * @property {string} deleted - Indicates whether the atlas is in the trash or not.
 * @property {string} name - The name for the atlas.
 * @property {string} owner - The owner id.
 * @property {boolean} shared - Indicates whether it is being shared or not.
 * @property {boolean} sharedPrivate - Indicates whether it is being shared or not.
 * @property {boolean} sharedUser - Indicates whether it is being shared or not.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} [description] - The description for the atlas.
 * @property {string} [gmsConnection] - The connection id for functional atlas.
 * @property {string} [gmsConnection2] - The connection id for functional atlas.
 * @property {string} [imageUrl] - The url for the image thumbnail.
 * @property {string} [publicId] - The public key generated for the URL.
 * @property {boolean} [showButtons] -The flag that enables the atlas buttons.
 * @property {object} sharedConfiguration - Indicates whether it is being shared with user or not.

 */

/**
 * The name of the atlas element and the props to pass.
 *
 * @typedef {object} atlasElementAndProps
 * @property {string} atlasElement - The name of the atlas element.
 * @property {atlasProp[]} atlasProps - The props needed to correctly create the element.
 */

/**
 * The name and the value of the props to pass.
 *
 * @typedef {object} atlasProp
 * @property {string} propName - The name of the prop.
 * @property {string|atlasNodeProp} propValue - The value of the prop or an object if the prop is a node.
 */

/**
 * The name of the child element and the props to pass.
 *
 * @typedef {object} atlasNodeProp
 * @property {string} elementName - The name of the child element.
 * @property {atlasProp[]} elementProps - The props of the child element.
 */

const Atlas = new Mongo.Collection('Atlas');

Atlas.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Atlas.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const atlasNodePropSchema = new SimpleSchema({
  elementName: {
    type: String,
    label: 'The name of the node element'
  },
  elementProps: {
    type: Array,
    label: 'The props of the element'
  },
  'elementProps.$': {
    type: Object,
    label: 'Each prop of the element'
  },
  'elementProps.$.propName': {
    type: String,
    label: 'The name of the prop'
  },
  'elementProps.$.propValue': {
    type: String,
    label: 'The value of the prop',
    optional: true
  }
});

const atlasPropSchema = new SimpleSchema({
  propName: {
    type: String,
    label: 'The name of the prop'
  },
  propValue: {
    type: SimpleSchema.oneOf(String, atlasNodePropSchema),
    label: 'The value of the prop or an object if the prop is a node',
    optional: true
  }
});

const atlasElementSchema = new SimpleSchema({
  atlasElement: {
    type: String,
    label: '',
    allowedValues: map(ATLAS_ELEMENTS.properties, 'value')
  },
  atlasProps: {
    type: Array,
    label: 'The props needed to correctly create the element'
  },
  'atlasProps.$': {
    type: atlasPropSchema,
    label: 'The name and the value of the props to pass'
  }
});

const sharedConfigurationSchema = new SimpleSchema({
  isNameShared: {
    type: Boolean,
    label: 'An object where the components of map are to be saved Teide',
    optional: true
  },
  userList: {
    type: Array,
    label: 'Flag that checks if map is shared with user or not.',
    optional: true
  },
  'userList.$': {
    type: Object,
    label: 'An object containing the id of the layer and the transparency value'
  },
  'userList.$.id': {
    type: String,
    label: 'The name of the user to share the map'
  },
  'userList.$.user': {
    type: String,
    label: 'The name of the user to share the map'
  },
  'userList.$.secondVerification': {
    type: Boolean,
    label: 'If the user needs a second verification in order to enter the map'
  },
  'userList.$.password': {
    type: String,
    label: 'The password of the user to share the map'
  }
});

Atlas.schema = new SimpleSchema({
  atlasStructure: {
    type: Array,
    label: 'The selected structure for the atlas type'
  },
  'atlasStructure.$': {
    type: atlasElementSchema,
    label: 'Each element of the structure'
  },
  atlasType: {
    type: String,
    label: 'The type of the graphic added',
    allowedValues: map(ATLAS_TEMPLATES.properties, 'value')
  },
  createdAt: {
    type: String,
    label: 'The date this atlas was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the atlas was deleted.',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  description: {
    type: String,
    label: 'A description of the atlas.',
    optional: true
  },
  gmsConnection: {
    type: String,
    label: 'The connection id for functional atlas.',
    optional: true
  },
  gmsConnection2: {
    type: String,
    label: 'The connection id for functional atlas.',
    optional: true
  },
  imageUrl: {
    type: String,
    optional: true,
    label: 'The url of an image to use as thumbnail'
  },
  name: {
    type: String,
    label: 'A name to identify the atlas.'
  },
  owner: {
    type: String,
    label: 'The ID of the user this atlas belongs to.'
  },
  publicId: {
    type: String,
    label: 'The public key generated for the URL',
    optional: true,
    autoValue() {
      const publicId = get(Atlas.findOne(this.docId), 'publicId');
      if (this.isInsert || (this.isUpdate && isNil(publicId))) return faker.internet.password();
    }
  },
  shared: {
    type: Boolean,
    label: 'Flag that checks if atlas is shared',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  sharedPrivate: {
    type: Boolean,
    label: 'Flag that checks if atlas is shared Private',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  sharedUser: {
    type: Boolean,
    label: 'Flag that checks if atlas is shared user',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  showButtons: {
    type: Boolean,
    label: 'Flag that checks if atlas shows the buttons',
    optional: true,
    autoValue() {
      if (this.isInsert) return true;
    }
  },
  sharedConfiguration: {
    type: sharedConfigurationSchema,
    label: 'Flag that checks if map is shared with user or not.',
    optional: true
  },
  updatedAt: {
    type: String,
    label: 'The date this atlas was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  }
});

Atlas.attachSchema(Atlas.schema);

export default Atlas;
